import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import NextAction from "../../utils/NextAction";
import { Link } from "react-router-dom";

interface ContrlProps {
  showDistance: boolean;
  setShowDistance: React.Dispatch<React.SetStateAction<boolean>>;
  cleanAllPoints: () => void;
}

export default function Controls({
  showDistance,
  setShowDistance,
  cleanAllPoints,
}: ContrlProps) {
  return (
    <div className="controls">
      <div>
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Voronoi Diagram
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here, we introduce the Voronoi Diagram, which is one of the methods
          used to build the roadmap in robotics for motion planning. This is the
          method mentioned in 12.3 in the book:{" "}
          <a href="https://link.springer.com/book/10.1007/978-1-84628-642-1">
            Robotics Modelling, Planning and Control
          </a>
          .
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          A Voronoi diagram is a fundamental geometric structure that partitions
          a plane into regions based on the distance to a specified set of
          points. Each region, known as a Voronoi cell,{" "}
          <b>
            consists of all points closer to its corresponding point, called a
            site, than to any other.
          </b>
          <br />
          <br />
          In motion planning, we can{" "}
          <b>
            treat each obstacle as a site in a Voronoi cell and then draw the
            Voronoi diagram
          </b>
          . By using the Voronoi edges, which are equidistant paths from the
          nearest obstacles, robots or agents can{" "}
          <b>plan their movements along these edges</b> to avoid collisions,
          ensuring smoother and safer navigation through complex terrains.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Feel free to click on the right screen to see the changes in the
          Voronoi diagram.
        </Typography>
        <FormControlLabel
          sx={{ mt: 1, mb: 2, textAlign: "left" }}
          control={
            <Checkbox
              checked={showDistance}
              onClick={() => {
                setShowDistance(!showDistance);
              }}
            />
          }
          label={"Show distances between the mouse and the closest points"}
        />
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The grey lines are the Voronoi edges and the black points are the
          Voronoi vertices. The vertices and the edges build a graph topology.
        </Typography>
        <Button variant={"contained"} sx={{ mb: 4 }} onClick={cleanAllPoints}>
          Clean All Points
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          There are three challenges for motion planning using Voronoi diagrams:
          <br />
          <br />
          1. How to draw the Voronoi diagram given a set of points?
          <br />
          2. In the real world, obstacles are polygons, not just points. How can
          we apply Voronoi diagrams to real-world scenarios?
          <br />
          3. How to decide the correct path along the Voronoi edges?
          <br />
        </Typography>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          Question 1
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          There are several algorithms for addressing Question 1, including
          half-plane intersection, the incremental method, divide and conquer,
          and more. The most famous approach is <b>Fortune's Algorithm</b> which
          is fast and practical. There are two really great visualization
          websites <br />
          1.{" "}
          <a href="https://www.cs.tufts.edu/comp/163/demos/fortune/">
            Voronoi Diagram construction using Fortune's Algorithm
          </a>{" "}
          <br />
          2.{" "}
          <a href="https://www.desmos.com/calculator/ejatebvup4">
            Fortune's Algorithm (for Voronoi diagrams)
          </a>{" "}
          <br />I will not reinvent the wheel currently.
        </Typography>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          Question 2
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          This issue is addressed by a concept called the{" "}
          <b>Generalized Voronoi Diagram</b>. We will introduce it on our next
          page.
        </Typography>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          Question 3
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          If we treat all Voronoi edges and vertices as a graph topology, we can
          use graph pathfinding algorithms, including{" "}
          <b>
            the <Link to="/robotics/dijkstras">Dijkstra's algorithm</Link> and
            the <Link to="/robotics/astar-for-grids">A* algorithm</Link>
          </b>
          , to find the path. We will visualize these pathfinding algorithms on
          another page.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Let's start from Generalized Voronoi Diagram!
        </Typography>
        <NextAction
          label={"Next: Generalized Voronoi Diagram"}
          to="/robotics/generalized-voronoi-diagram"
        />
      </div>
    </div>
  );
}
